import React, { Dispatch } from 'react'
import { IMyAccountContext } from '../../hooks/useMyAccountContext'
import { UserSettingEvent } from '../../UserSetting'
import { Logger } from 'typescript-log'
import { BaseClientConfig, authCheckAccessToken } from '@news-mono/web-common'
import { ChangeEmailErrorProps } from '../ChangeEmail'

export type ChangeEmailBannerType =
    | 'success'
    | 'incorrect-password'
    | 'same-email'
    | 'invalid-email'
    | 'blocked-word-in-email'
    | 'forgot-password-success'
    | 'forgot-password-error'
    | 'unknown'

// Props to send through the error state & setState
interface ChangeEmailErrorBannerProps {
    errors: ChangeEmailErrorProps
    setErrors: React.Dispatch<React.SetStateAction<ChangeEmailErrorProps>>
}
// Props used to send through to all banner helpers
export interface BannerHelperProps {
    // Context values below:
    myAccountContext: IMyAccountContext
    // State values below:
    errorState: ChangeEmailErrorBannerProps
    // Dispatch and required values below:
    dispatch: Dispatch<any>
    log: Logger
    config: BaseClientConfig
    onEvent: (e: UserSettingEvent) => void
}

export const getAndFireAppropriateBannerHelper = (
    type: ChangeEmailBannerType,
    props: BannerHelperProps,
    text?: string,
) => {
    switch (type) {
        case 'success':
            getSuccessfulEmailChangeHandler(props)
            return
        case 'incorrect-password':
            getIncorrectPasswordHandler(props)
            return
        case 'same-email':
            getSameEmailHandler(props)
            return
        case 'invalid-email':
            getIncorrectEmailHandler(props)
            return
        case 'blocked-word-in-email':
            getBlockedWordEmailHandler(props)
            return
        case 'forgot-password-success':
            getForgotPasswordSuccessHandler(
                props,
                text || 'Password reset email sent.',
            )
            return
        case 'forgot-password-error':
            getForgotPasswordErrorHandler(
                props,
                text || 'Unable to send password reset email.',
            )
            return
        case 'unknown':
            getUnknownErrorHandler(props)
            return
    }
}

// Show the success message banner and go back to the parent page.
const getSuccessfulEmailChangeHandler = (props: BannerHelperProps) => {
    const { myAccountContext, dispatch, onEvent, log, config } = props
    const { setToast, goBackToParent } = myAccountContext

    setToast({
        type: 'success',
        text: 'Email successfully updated.',
        show: true,
    })
    goBackToParent(false)

    // Refresh user info after updates
    dispatch(
        authCheckAccessToken({
            onEvent,
            log,
            config,
            invocation: 'auto',
            forceRefresh: true,
            onInvocationEvent: onEvent,
        }),
    )
}

// When there is an unhandled error, we want to show that something went wrong.
const getUnknownErrorHandler = (props: BannerHelperProps) => {
    const { myAccountContext, errorState } = props
    const { setMessageBanner } = myAccountContext
    const { setErrors } = errorState

    setMessageBanner({
        type: 'error',
        text: 'Failed to update email, please try again.',
    })
    setErrors({
        emailError: true,
        passwordError: true,
    })
}

// Whenever someone enters the password that doesn't equal their current password, send this to them.
const getIncorrectPasswordHandler = (props: BannerHelperProps) => {
    const { myAccountContext, errorState } = props
    const { setMessageBanner } = myAccountContext
    const { errors, setErrors } = errorState

    setMessageBanner({
        type: 'error',
        text: 'Incorrect password, please try again.',
    })
    setErrors({
        ...errors,
        passwordError: true,
    })
}

// Whenever someone tries to enter an email address that is the same as their current email, show them this error.
const getSameEmailHandler = (props: BannerHelperProps) => {
    const { myAccountContext, errorState } = props
    const { setMessageBanner } = myAccountContext
    const { errors, setErrors } = errorState

    setMessageBanner({
        type: 'error',
        text: 'You cannot use the same email address.',
    })
    setErrors({
        ...errors,
        emailError: true,
    })
}

// Whenever someone tries to enter an email address that doesn't meet the requirements, show them this error.
const getIncorrectEmailHandler = (props: BannerHelperProps) => {
    const { myAccountContext, errorState } = props
    const { setMessageBanner } = myAccountContext
    const { errors, setErrors } = errorState

    setMessageBanner({
        type: 'error',
        text: 'Incorrect email format, please try again.',
    })
    setErrors({
        ...errors,
        emailError: true,
    })
}

const getBlockedWordEmailHandler = (props: BannerHelperProps) => {
    const { myAccountContext, errorState } = props
    const { setMessageBanner } = myAccountContext
    const { errors, setErrors } = errorState

    setMessageBanner({
        type: 'error',
        text: 'You cannot use blocked words in your email address.',
    })
    setErrors({
        ...errors,
        emailError: true,
    })
}

const getForgotPasswordSuccessHandler = (
    props: BannerHelperProps,
    text: string,
) => {
    const { myAccountContext } = props
    const { setToast } = myAccountContext

    setToast({
        text,
        type: 'success',
        show: true,
    })
}

const getForgotPasswordErrorHandler = (
    props: BannerHelperProps,
    text: string,
) => {
    const { myAccountContext } = props
    const { setMessageBanner } = myAccountContext

    setMessageBanner({
        type: 'error',
        text,
    })
}
